import { isProduction } from '../../../constants/environment';
import type { Env } from '../../../context/env-context';
import baseCss from '../assets/snippets/base/css';
import bigCommerceCore from '../assets/snippets/bigcommerce/core';
import bigCommerceCss from '../assets/snippets/bigcommerce/css';
import customAllVariants from '../assets/snippets/custom/all-variants';
import customCard from '../assets/snippets/custom/card';
import customCore from '../assets/snippets/custom/core';
import customMatrix from '../assets/snippets/custom/matrix';
import customPdp from '../assets/snippets/custom/pdp';
import customPrice from '../assets/snippets/custom/price';
import customQuickBuy from '../assets/snippets/custom/quick-buy';
import shopifyAllVariants from '../assets/snippets/shopify/all-variants';
import shopifyCard from '../assets/snippets/shopify/card';
import shopifyCore from '../assets/snippets/shopify/core';
import shopifyMatrix from '../assets/snippets/shopify/matrix';
import shopifyPdp from '../assets/snippets/shopify/pdp';
import shopifyPrice from '../assets/snippets/shopify/price';
import shopifyQuickBuy from '../assets/snippets/shopify/quick-buy';
import wixCss from '../assets/snippets/wix/css';
import woocommerceAllVariants from '../assets/snippets/woocommerce/all-variants';
import woocommerceCard from '../assets/snippets/woocommerce/card';
import woocommerceCore from '../assets/snippets/woocommerce/core';
import woocommerceCss from '../assets/snippets/woocommerce/css';
import woocommerceMatrix from '../assets/snippets/woocommerce/matrix';
import woocommercePdp from '../assets/snippets/woocommerce/pdp';
import woocommercePrice from '../assets/snippets/woocommerce/price';
import woocommerceQuickBuy from '../assets/snippets/woocommerce/quick-buy';
import { SPARKJS_LEGACY_FALLBACK_VERSION } from '../constants';
import { isShopify } from './platform';

function pickByPlatform(
  platform: Env['platform'],
  variants: { shopify: string; woocommerce: string; base: string },
): string {
  if (isShopify(platform)) return variants.shopify;
  if (platform === 'woocommerce') return variants.woocommerce;
  return variants.base;
}

function sparkJsUrl(env: Env): string {
  const isLive = env.environment === 'live';
  if (!env.sparkjs_version) {
    return `https://cdn.sparklayer.io/spark.${SPARKJS_LEGACY_FALLBACK_VERSION}.js`;
  }

  const prefix = isProduction ? '' : 'dev.';
  return `https://${prefix}sparkcdn.io/sparkjs/${env.team.slugged_name}/${isLive ? 'live' : 'test'}`;
}

function domainCode(env: Env): string {
  const isLive = env.environment === 'live';
  if (isLive && isProduction) return '';

  const domain = isLive ? window.location.hostname : `test.${window.location.hostname}`;
  return `\n  sparkDomain: "${domain}",`;
}

export function buildCoreSnippet(env: Env): string {
  let code: string;
  switch (env.platform) {
    case 'shopify':
    case 'shopify_plus':
      code = shopifyCore;
      break;
    case 'bigcommerce':
      code = bigCommerceCore;
      break;
    case 'woocommerce':
      code = woocommerceCore;
      break;
    default:
      code = customCore;
  }

  code = code
    .replace('#SPARKJS_URL#', sparkJsUrl(env))
    .replace('#SITE_ID#', env.team.slugged_name)
    .replace('#DOMAIN#', domainCode(env));

  return `\n\n${code}\n`;
}

export function buildCssSnippet(env: Env): string {
  const css: Partial<Record<NonNullable<Env['platform']>, string>> = {
    bigcommerce: bigCommerceCss,
    wix: wixCss,
    woocommerce: woocommerceCss,
  };

  const code = (env.platform && css[env.platform]) || baseCss;
  return `\n\n${code}\n`;
}

export function buildPdpSnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, { shopify: shopifyPdp, woocommerce: woocommercePdp, base: customPdp })}\n`;
}

export function buildAllVariantsSnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, {
    shopify: shopifyAllVariants,
    woocommerce: woocommerceAllVariants,
    base: customAllVariants,
  })}\n`;
}

export function buildMatrixSnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, {
    shopify: shopifyMatrix,
    woocommerce: woocommerceMatrix,
    base: customMatrix,
  })}\n`;
}

export function buildCardSnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, { shopify: shopifyCard, woocommerce: woocommerceCard, base: customCard })}\n`;
}

export function buildPriceSnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, {
    shopify: shopifyPrice,
    woocommerce: woocommercePrice,
    base: customPrice,
  })}\n`;
}

export function buildQuickBuySnippet(platform: Env['platform']): string {
  return `\n\n${pickByPlatform(platform, {
    shopify: shopifyQuickBuy,
    woocommerce: woocommerceQuickBuy,
    base: customQuickBuy,
  })}\n`;
}
